import { OperationLog, OperationLogStatus } from '@/client/operation-log';
import { LoadingStateType } from '@/common/constants';
import {
  AdSyncError,
  extractUserIdentifier,
  normalizeErrorMessage,
  USER_ERROR_CODES,
} from '@/pages/accounts/account-errors/error-log.helpers';
import {
  ActionRow,
  Banner,
  BannerIcon,
  BannerText,
  BannerTitle,
  CategoryTitle,
  EmptyStateCard,
  EmptyStateContent,
  ErrorCategory,
  ErrorTable,
  SectionTitle,
  TableLog,
} from '@/pages/accounts/account-errors/error-log.styled';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import React, { useMemo } from 'react';

type Props = {
  log: OperationLog | undefined;
  t: (key: string, params?: Record<string, unknown>) => string;
  onSync: () => void;
  syncState: LoadingStateType;
  onGoToSettings: () => void;
};

export const AdSyncErrorsSection: React.FC<Props> = ({
  log,
  t,
  onSync,
  syncState,
  onGoToSettings,
}) => {
  const errors = (log?.errors ?? []) as AdSyncError[];
  const hasErrors =
    log && log.status !== OperationLogStatus.SUCCESS && errors.length > 0;

  const groupedByType = useMemo(() => {
    const groups: Record<string, { user: string; message: string }[]> = {};

    errors.forEach((error) => {
      if (Array.isArray(error)) {
        const [message, errorCode, params] = error as [
          string,
          string,
          unknown[],
        ];
        const label = errorCode || 'general';
        const userIdentifier = extractUserIdentifier(label, params);

        if (!groups[label]) {
          groups[label] = [];
        }

        groups[label].push({ user: userIdentifier, message: String(message) });
      } else if (typeof error === 'string') {
        const label = 'general';

        if (!groups[label]) {
          groups[label] = [];
        }

        groups[label].push({ user: '-', message: error });
      }
    });

    return groups;
  }, [errors]);

  const getErrorTypeLabel = (errorCode: string): string => {
    const labelMap: Record<string, string> = {
      'user.already.assigned.to.branch': t(
        'errorLog.adSync.alreadyAssignedToBranch',
      ),
      'user.already.exists.in.another.account': t(
        'errorLog.adSync.alreadyExistsInAnotherAccount',
      ),
      'user.no.email.provided': t('errorLog.adSync.noEmailProvided'),
      'user.duplicate.username': t('errorLog.adSync.duplicateUsername'),
      general: t('errorLog.adSync.general'),
      '404': t('errorLog.adSync.groupNotFound'),
    };

    return labelMap[errorCode] || errorCode;
  };

  return (
    <FlexContainer direction="column" gap={12} align="flex-start">
      <SectionTitle>{t('errorLog.adErrors')}</SectionTitle>

      {!hasErrors && (
        <>
          <Banner variant="success">
            <BannerIcon variant="success">
              <i className="pi pi-check" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>{t('errorLog.adSyncSuccess')}</BannerTitle>
            </BannerText>
          </Banner>
          <EmptyStateCard>
            <EmptyStateContent>
              <i className="pi pi-check-circle" />
              <span>{t('errorLog.noCurrentErrors')}</span>
            </EmptyStateContent>
          </EmptyStateCard>
        </>
      )}

      {hasErrors && (
        <>
          <Banner variant="warning">
            <BannerIcon variant="warning">
              <i className="pi pi-exclamation-circle" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>
                {t('errorLog.adSyncCompleted', { count: log.errorCount })}
              </BannerTitle>
              <span>{t('errorLog.adSyncResolveHint')}</span>
            </BannerText>
          </Banner>

          <TableLog>
            {Object.entries(groupedByType).map(([errorCode, entries]) => {
              const isUserError = USER_ERROR_CODES.includes(errorCode);

              return (
                <ErrorCategory key={errorCode}>
                  <FlexContainer justify="space-between" align="center">
                    <CategoryTitle>
                      {getErrorTypeLabel(errorCode)} ({entries.length})
                    </CategoryTitle>
                  </FlexContainer>
                  <ErrorTable>
                    <thead>
                      <tr>
                        {isUserError && <th>{t('errorLog.user')}</th>}
                        <th>{t('errorLog.error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, index) => (
                        <tr key={index}>
                          {isUserError && <td>{entry.user}</td>}
                          <td>
                            {isUserError
                              ? getErrorTypeLabel(errorCode)
                              : normalizeErrorMessage(entry.message)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </ErrorTable>
                </ErrorCategory>
              );
            })}
          </TableLog>
        </>
      )}

      <ActionRow>
        <AppButton
          label={t('errorLog.rerunSync')}
          icon="pi pi-sync"
          onClick={onSync}
          state={syncState}
        />
        <AppButton
          label={t('errorLog.goToSettings')}
          type="outlined"
          onClick={onGoToSettings}
        />
      </ActionRow>
    </FlexContainer>
  );
};
